/**
 * StandaloneWaterDispenserV3 — Earthy Oasis Light Theme
 *
 * Fullscreen standalone layout for Acuo App (not a popup modal).
 * Renders inline as dashboard content.
 *
 * category: 'standalone'
 */
import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import type { DeviceModalTemplateProps, ModalSectionMeta } from '../types/deviceModalTemplate'
import { executeCommandWithAuth, configureDeviceNicknameWithAuth } from '../services/turingMcpApi'
import './StandaloneWaterDispenserV3.css'

export const WATER_DISPENSER_V3_SECTIONS: ModalSectionMeta[] = [
  { id: '_modal', label: 'Background', icon: 'smartphone', slots: [] },
  { id: 'header', label: 'Device Header', icon: 'title', slots: [
    { id: 'header-name', label: 'Device Name', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing', 'background', 'padding', 'borderRadius'] },
    { id: 'header-status', label: 'Status Text', editableProps: ['fontSize', 'color', 'fontWeight', 'background', 'padding', 'borderRadius'] },
    { id: 'header-dot', label: 'Status Dot', editableProps: ['background', 'width', 'height', 'borderRadius', 'border'] },
    { id: 'header-eco-icon', label: 'Eco Icon', editableProps: ['fontSize', 'color', 'background', 'padding', 'borderRadius'] },
    { id: 'header-eco-msg', label: 'Eco Message', editableProps: ['fontSize', 'color', 'fontWeight', 'background', 'padding'] },
    { id: 'header-eco-stat', label: 'Eco Stat', editableProps: ['fontSize', 'color', 'fontWeight'] },
    { id: 'header-eco-stat-label', label: 'Eco Stat Label', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing'] },
  ]},
  { id: 'purity', label: 'Hydration Status', icon: 'water_drop', slots: [
    { id: 'purity-label', label: 'Status Label', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing', 'background', 'padding'] },
    { id: 'purity-number', label: 'Liters Value', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing', 'background', 'padding'] },
    { id: 'purity-unit', label: 'Unit Text', editableProps: ['fontSize', 'color', 'fontWeight', 'background', 'padding'] },
    { id: 'purity-bar', label: 'Progress Bar', editableProps: ['background', 'borderRadius', 'height'] },
    { id: 'purity-range', label: 'Range Labels', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing', 'background', 'padding'] },
  ]},
  { id: 'status', label: 'Status Card', icon: 'opacity', slots: [
    { id: 'status-label', label: 'Status Label', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing', 'background', 'padding'] },
    { id: 'status-icon', label: 'Status Icon', editableProps: ['fontSize', 'color', 'background', 'padding', 'borderRadius'] },
    { id: 'status-mode', label: 'Outlet Card', editableProps: ['background', 'padding', 'borderRadius', 'border'] },
    { id: 'status-outlet-icon', label: 'Outlet Icon', editableProps: ['fontSize', 'color'] },
    { id: 'status-outlet-temp', label: 'Outlet Temp', editableProps: ['fontSize', 'color', 'fontWeight'] },
    { id: 'status-outlet-level', label: 'Water Level Bar', editableProps: ['background', 'borderRadius', 'height'] },
    { id: 'status-outlet-label', label: 'Outlet Label', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing'] },
  ]},
  { id: 'tds', label: 'Water Purity (TDS)', icon: 'water_drop', slots: [
    { id: 'tds-icon', label: 'TDS Icon', editableProps: ['fontSize', 'color', 'background', 'padding', 'borderRadius'] },
    { id: 'tds-label', label: 'Section Label', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing', 'background', 'padding'] },
    { id: 'tds-pure-value', label: 'Pure Water Value', editableProps: ['fontSize', 'color', 'fontWeight', 'background', 'padding'] },
    { id: 'tds-raw-value', label: 'Source Water Value', editableProps: ['fontSize', 'color', 'fontWeight', 'background', 'padding'] },
  ]},
  { id: 'controls', label: 'Temperature Controls', icon: 'thermostat', slots: [
    { id: 'controls-title', label: 'Section Title', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing', 'background', 'padding'] },
    { id: 'controls-icon', label: 'Section Icon', editableProps: ['fontSize', 'color', 'background', 'padding', 'borderRadius'] },
    { id: 'controls-name', label: 'Outlet Name', editableProps: ['fontSize', 'color', 'fontWeight', 'background', 'padding'] },
    { id: 'controls-range', label: 'Temp Value', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing', 'background', 'padding'] },
    { id: 'controls-slider', label: 'Slider', editableProps: ['background', 'height', 'borderRadius'] },
    { id: 'controls-btn', label: 'Re-Boil Button', editableProps: ['fontSize', 'color', 'fontWeight', 'background', 'padding', 'borderRadius', 'border', 'letterSpacing'] },
  ]},
  { id: 'chart', label: 'Consumption History', icon: 'bar_chart', slots: [
    { id: 'chart-label', label: 'Section Label', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing', 'background', 'padding'] },
    { id: 'chart-title', label: 'Chart Title', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing', 'background', 'padding'] },
    { id: 'chart-range-btn', label: 'Range Button', editableProps: ['fontSize', 'color', 'fontWeight', 'background', 'padding', 'borderRadius'] },
    { id: 'chart-bar', label: 'Bar', editableProps: ['background', 'borderRadius', 'border'] },
  ]},
  { id: 'childLock', label: 'Child Lock', icon: 'lock', slots: [
    { id: 'lock-icon', label: 'Lock Icon', editableProps: ['fontSize', 'color', 'background', 'padding', 'borderRadius'] },
    { id: 'lock-label', label: 'Lock Label', editableProps: ['fontSize', 'color', 'fontWeight', 'background', 'padding', 'borderRadius'] },
    { id: 'lock-toggle', label: 'Toggle Switch', editableProps: ['background', 'borderRadius', 'width', 'height'] },
  ]},
  { id: 'filters', label: 'Filter Status', icon: 'filter_alt', slots: [
    { id: 'filter-name', label: 'Filter Name', editableProps: ['fontSize', 'color', 'fontWeight', 'background', 'padding'] },
    { id: 'filter-pct', label: 'Percentage', editableProps: ['fontSize', 'color', 'fontWeight', 'background', 'padding'] },
    { id: 'filter-btn', label: 'Filter Icon', editableProps: ['fontSize', 'color', 'background', 'padding', 'borderRadius'] },
    { id: 'filter-desc', label: 'Performance Text', editableProps: ['fontSize', 'color', 'fontWeight', 'background', 'padding'] },
    { id: 'filter-order', label: 'Order Link', editableProps: ['fontSize', 'color', 'fontWeight', 'background', 'padding', 'borderRadius'] },
  ]},
]

const DEFAULT_SECTION_ORDER = WATER_DISPENSER_V3_SECTIONS.map(s => s.id)

// ---------------------------------------------------------------------------
// Helpers (same as V2)
// ---------------------------------------------------------------------------
function getAttr(state: Record<string, unknown> | null, ...keys: string[]): unknown {
  if (!state) return undefined
  for (const key of keys) { if (key in state && state[key] !== undefined && state[key] !== null) return state[key] }
  return undefined
}
function numAttr(state: Record<string, unknown> | null, ...keys: string[]): number | null {
  let v = getAttr(state, ...keys)
  if (v === undefined || v === null) return null
  if (Array.isArray(v)) v = v[0]
  if (v === undefined || v === null) return null
  const n = Number(v); return isNaN(n) ? null : n
}

/**
 * Normalize raw MCU state fields into camelCase keys expected by OUTLET_DEFS.
 *
 * MCU field notes (from GetGroupDeviceStates):
 *   - *_display_temp / *_actual_temp / old *_temp: raw ×10 (e.g. 972 = 97.2°C)
 *   - *_setpoint: already in °C (no ÷10 needed)
 *   - filter*_months: months of life remaining for each filter stage
 *   - total_flow / left_dispense / right_dispense: flow in unknown unit (0 for this device)
 */
function normalizeWaterDispenserState(
  state: Record<string, unknown> | null
): Record<string, unknown> | null {
  if (!state) return null
  const normalized: Record<string, unknown> = { ...state }

  // ── Temperature display (÷10) ────────────────────────────────────────────
  // Priority: display_temp > actual_temp > legacy sensor _temp
  const TEMP_SCALED: Array<[string, string]> = [
    ['hot_display_temp',  'hotTemp'],
    ['hot_actual_temp',   'hotTemp'],
    ['hot_temp',          'hotTemp'],
    ['cool_display_temp', 'iceTemp'],
    ['cool_actual_temp',  'iceTemp'],
    ['cool_temp',         'iceTemp'],
    ['warm_display_temp', 'warmTemp'],
    ['warm_actual_temp',  'warmTemp'],
    ['warm_temp',         'warmTemp'],
  ]
  for (const [rawKey, camelKey] of TEMP_SCALED) {
    if (rawKey in state && state[rawKey] != null && !(camelKey in normalized)) {
      const raw = Number(state[rawKey])
      if (!isNaN(raw)) normalized[camelKey] = Math.round(raw) / 10
    }
  }

  // ── Setpoints (already °C, no scaling) ──────────────────────────────────
  const SETPOINTS: Array<[string, string]> = [
    ['hot_setpoint',  'hotTargetTemp'],
    ['cool_setpoint', 'iceTargetTemp'],
    ['warm_setpoint', 'warmTargetTemp'],
  ]
  for (const [rawKey, camelKey] of SETPOINTS) {
    if (rawKey in state && state[rawKey] != null && !(camelKey in normalized)) {
      const v = Number(state[rawKey])
      if (!isNaN(v) && v > 0) normalized[camelKey] = v
    }
  }

  // ── Filter life (months remaining → rough %) ────────────────────────────
  // Assume typical life: filter1/2 = 6 mo PP, filter3/4 = 12 mo carbon/RO
  const FILTER_MAX: Record<string, number> = {
    filter1_months: 6, filter2_months: 6,
    filter3_months: 12, filter4_months: 12,
  }
  const filterPcts: number[] = []
  for (const [key, maxMonths] of Object.entries(FILTER_MAX)) {
    if (key in state && state[key] != null) {
      const remaining = Number(state[key])
      if (!isNaN(remaining)) filterPcts.push(Math.round((remaining / maxMonths) * 100))
    }
  }
  if (filterPcts.length > 0 && !('filterLife' in normalized)) {
    // Show worst (minimum) filter life percentage
    normalized.filterLife = Math.min(...filterPcts)
  }

  // ── Consumption ─────────────────────────────────────────────────────────
  if ('total_flow' in state && !('dailyConsumption' in normalized)) {
    const flow = Number(state.total_flow)
    if (!isNaN(flow)) normalized.dailyConsumption = flow
  }

  // ── Child lock ───────────────────────────────────────────────────────────
  // hot_lock_mode: 0 = unlocked, 1 = locked
  if ('hot_lock_mode' in state && !('childLock' in normalized)) {
    normalized.childLock = Number(state.hot_lock_mode) > 0
  }

  // ── TDS ─────────────────────────────────────────────────────────────────
  // tds_pure = purified water TDS, tds_raw = source water TDS
  if ('tds_pure' in state && !('tds' in normalized)) {
    const v = Number(state.tds_pure)
    if (!isNaN(v)) normalized.tds = v
  }

  // Water dispenser has no on/off power state — default to on so temps are visible
  if (!('on' in normalized)) normalized.on = true

  return normalized
}

// ---------------------------------------------------------------------------
// Outlet + Filter definitions (same as V1/V2)
// ---------------------------------------------------------------------------
type TempOutlet = {
  id: string; label: string; rangeLabel: string
  tempAttrKeys: string[]; targetAttrKeys: string[]; levelAttrKeys: string[]
  setCmd: string; min: number; max: number; step: number; pickerTitle: string
}
const OUTLET_DEFS: TempOutlet[] = [
  { id: 'ice', label: 'Cold', rangeLabel: '4°C — 8°C',
    tempAttrKeys: ['iceTemp', 'icewaterTemperature', 'coldTemp'],
    targetAttrKeys: ['iceTargetTemp', 'icewaterTargetTemperature', 'coldTargetTemp'],
    levelAttrKeys: ['iceTankLevel', 'icewaterLevel', 'coldTankLevel'],
    setCmd: 'setIceTemp', min: 3, max: 10, step: 1, pickerTitle: 'Cold Target Temp' },
  { id: 'warm', label: 'Ambient', rangeLabel: 'Natural',
    tempAttrKeys: ['warmTemp', 'warmwaterTemperature'],
    targetAttrKeys: ['warmTargetTemp', 'warmwaterTargetTemperature'],
    levelAttrKeys: ['warmTankLevel', 'warmwaterLevel'],
    setCmd: 'setWarmTemp', min: 35, max: 55, step: 1, pickerTitle: 'Warm Target Temp' },
  { id: 'hot', label: 'Hot', rangeLabel: '85°C — 98°C',
    tempAttrKeys: ['hotTemp', 'hotwaterTemperature'],
    targetAttrKeys: ['hotTargetTemp', 'hotwaterTargetTemperature'],
    levelAttrKeys: ['hotTankLevel', 'hotwaterLevel'],
    setCmd: 'setHotTemp', min: 80, max: 100, step: 1, pickerTitle: 'Hot Target Temp' },
]

type FilterDef = { id: string; name: string; attrKeys: string[] }
const FILTER_DEFS: FilterDef[] = [
  { id: 'pp', name: 'PP Filter', attrKeys: ['filterLifePP', 'ppFilterLife'] },
  { id: 'carbon', name: 'Carbon Filter', attrKeys: ['filterLifeCarbon', 'carbonFilterLife'] },
  { id: 'ro', name: 'RO Membrane', attrKeys: ['filterLifeRO', 'roFilterLife'] },
  { id: 'generic', name: 'Filter', attrKeys: ['filterLife'] },
]

function filterStatus(pct: number) {
  if (pct >= 50) return { cls: 'wd3__filter-pct--good', fillCls: 'wd3__filter-fill--good' }
  if (pct >= 30) return { cls: 'wd3__filter-pct--warn', fillCls: 'wd3__filter-fill--warn' }
  return { cls: 'wd3__filter-pct--low', fillCls: 'wd3__filter-fill--low' }
}

// Mock state
const MOCK_STATE: Record<string, unknown> = {
  on: true,
  iceTemp: 4, iceTargetTemp: 5, iceTankLevel: 84,
  warmTemp: 25, warmTankLevel: 62,
  hotTemp: 92, hotTargetTemp: 95, hotTankLevel: 45,
  filterLife: 92, tds: 8,
  childLock: false,
  dailyConsumption: 1200,       // ml today
  monthlyConsumption: 36000,    // ml this month
  lifetimeVolume: 12400000,     // ml lifetime (12,400 L)
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function StandaloneWaterDispenserV3({
  device, deviceState, onCommand, onClose, isEditorMode, cssOverrides,
  sectionOrder, sectionVisible, highlightedSection, credentials,
}: DeviceModalTemplateProps) {

  const orderedSections = useMemo(() => {
    const order = sectionOrder?.length ? sectionOrder : DEFAULT_SECTION_ORDER
    return [...order, ...DEFAULT_SECTION_ORDER.filter(id => !order.includes(id))]
  }, [sectionOrder])
  const isSectionVisible = useCallback((id: string) => sectionVisible?.[id] !== false, [sectionVisible])

  // Effective state (mock fallback — editor mode only)
  // Normalize snake_case sensor keys (hot_temp/cool_temp/warm_temp) to camelCase
  const normalizedState = normalizeWaterDispenserState(deviceState)
  const allTempKeys = OUTLET_DEFS.flatMap(d => d.tempAttrKeys)
  const hasAnyTempKey = normalizedState && allTempKeys.some(k => k in normalizedState && normalizedState[k] != null)
  // NEVER fall back to mock data on real devices — mock only in editor/demo mode
  const isMock = isEditorMode && (!normalizedState || Object.keys(normalizedState).length === 0 || !hasAnyTempKey)
  const effectiveState = isMock ? { ...MOCK_STATE, ...(normalizedState || {}) } : (normalizedState || {})

  const isOn = Boolean(getAttr(effectiveState, 'on'))
  const childLock = Boolean(getAttr(effectiveState, 'childLock', 'lockState'))
  const tds = numAttr(effectiveState, 'tds', 'tdsValue', 'waterPurity')

  const activeOutlets = useMemo(() => {
    const found: TempOutlet[] = []
    for (const def of OUTLET_DEFS) {
      if (def.tempAttrKeys.some(k => effectiveState && k in effectiveState && effectiveState[k] != null)) found.push(def)
    }
    return found.length > 0 ? found : OUTLET_DEFS
  }, [effectiveState])

  const activeFilters = useMemo(() => {
    const found: Array<FilterDef & { pct: number }> = []
    for (const def of FILTER_DEFS) {
      const val = numAttr(effectiveState, ...def.attrKeys)
      if (val !== null) found.push({ ...def, pct: val })
    }
    return found
  }, [effectiveState])

  const [selectedOutletId, setSelectedOutletId] = useState(activeOutlets[0]?.id || 'ice')
  const selectedOutlet = activeOutlets.find(o => o.id === selectedOutletId) || activeOutlets[0]
  const selectedTemp = numAttr(effectiveState, ...selectedOutlet.tempAttrKeys)
  const selectedTarget = numAttr(effectiveState, ...selectedOutlet.targetAttrKeys)

  // Pending state
  const [pendingPower, setPendingPower] = useState<boolean | null>(null)
  const [pendingLock, setPendingLock] = useState<boolean | null>(null)
  const displayPower = pendingPower ?? isOn
  const displayLock = pendingLock ?? childLock
  useEffect(() => { if (pendingPower !== null && pendingPower === isOn) setPendingPower(null) }, [isOn, pendingPower])
  useEffect(() => { if (pendingLock !== null && pendingLock === childLock) setPendingLock(null) }, [childLock, pendingLock])

  // Pending slider targets (local UI state before server confirms)
  const [pendingTargets, setPendingTargets] = useState<Record<string, number>>({})

  const execCmd = useCallback((cmd: string, params?: Record<string, unknown>) => {
    if (!credentials || !device.sn) return
    executeCommandWithAuth(credentials, [device.sn], cmd, params).catch(() => {})
    if (onCommand) onCommand(cmd, params)
  }, [credentials, device.sn, onCommand])

  const toggleChildLock = useCallback(() => {
    if (isEditorMode) return
    const next = !displayLock; setPendingLock(next); execCmd('setChildLock', { lock: next })
  }, [isEditorMode, displayLock, execCmd])

  // Picker
  const [pickerOpen, setPickerOpen] = useState(false)
  const [pickerOutlet, setPickerOutlet] = useState<TempOutlet | null>(null)
  const [pickerValue, setPickerValue] = useState(0)
  const pickerWheelRef = useRef<HTMLDivElement>(null)

  const openPicker = useCallback((outlet: TempOutlet) => {
    if (!displayPower) return
    const val = numAttr(effectiveState, ...outlet.targetAttrKeys) ?? numAttr(effectiveState, ...outlet.tempAttrKeys) ?? outlet.min
    setPickerOutlet(outlet); setPickerValue(val); setPickerOpen(true)
  }, [displayPower, effectiveState])

  const confirmPicker = useCallback(() => {
    if (pickerOutlet) execCmd(pickerOutlet.setCmd, { temperature: pickerValue })
    setPickerOpen(false); setPickerOutlet(null)
  }, [pickerOutlet, pickerValue, execCmd])

  const pickerItems = useMemo(() => {
    if (!pickerOutlet) return []
    const items: number[] = []
    for (let t = pickerOutlet.min; t <= pickerOutlet.max; t += pickerOutlet.step) items.push(t)
    return items
  }, [pickerOutlet])

  useEffect(() => {
    if (!pickerOpen || !pickerWheelRef.current) return
    const idx = pickerItems.indexOf(pickerValue)
    if (idx >= 0) setTimeout(() => { pickerWheelRef.current?.scrollTo({ top: idx * 40 }) }, 60)
  }, [pickerOpen, pickerValue, pickerItems])

  // Chart mock data
  const [chartRange, setChartRange] = useState<'day' | 'week' | 'month'>('day')
  const chartData = useMemo(() => {
    const n = chartRange === 'day' ? 12 : chartRange === 'week' ? 7 : 30
    return Array.from({ length: n }, (_, i) => ({
      pct: 20 + Math.random() * 75,
      active: chartRange === 'day' ? i === 4 : i === n - 3,
    }))
  }, [chartRange])

  // Display
  // ── Settings panel ──
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [isRenaming, setIsRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState('')
  const [displayName, setDisplayName] = useState(device.name || device.sn)
  const renameInputRef = useRef<HTMLInputElement>(null)
  useEffect(() => { setDisplayName(device.name || device.sn) }, [device.name, device.sn])

  const startRename = useCallback(() => {
    setRenameValue(displayName); setIsRenaming(true)
    setTimeout(() => renameInputRef.current?.focus(), 50)
  }, [displayName])

  const submitRename = useCallback(async () => {
    const trimmed = renameValue.trim()
    if (!trimmed || trimmed === displayName || !credentials) { setIsRenaming(false); return }
    setDisplayName(trimmed); setIsRenaming(false)
    await configureDeviceNicknameWithAuth(credentials, device.sn, trimmed).catch(() => {})
  }, [renameValue, displayName, credentials, device.sn])

  const [copiedField, setCopiedField] = useState<string | null>(null)
  const copyToClipboard = useCallback((text: string, label: string) => {
    navigator.clipboard.writeText(text).catch(() => {})
    setCopiedField(label); setTimeout(() => setCopiedField(null), 1000)
  }, [])

  const deviceInfoItems = useMemo(() => {
    const items: Array<{ label: string; value: string; action?: 'copy' }> = []
    items.push({ label: 'DEVICE TYPE', value: String(device.type || 'Unknown') })
    if ((device as Record<string, unknown>).ip) items.push({ label: 'IP ADDRESS', value: String((device as Record<string, unknown>).ip) })
    if (device.fwVersion || device.firmwareVersion) items.push({ label: 'FW VERSION', value: String(device.fwVersion || device.firmwareVersion) })
    items.push({ label: 'DEVICE SN', value: device.sn, action: 'copy' })
    return items
  }, [device])
  const stateClass = displayPower ? 'wd3--on' : 'wd3--off'
  const hl = (id: string) => highlightedSection === id ? ' section-highlighted' : ''
  const statusLabel = selectedOutletId === 'ice' ? 'Cooling' : selectedOutletId === 'hot' ? 'Heating' : 'Ambient'

  const renderSection = (sectionId: string) => {
    if (!isSectionVisible(sectionId)) return null
    switch (sectionId) {
      case 'header': {
        const monthlyMl = numAttr(effectiveState, 'monthlyConsumption', 'monthlyVolume') ?? 0
        const lifetimeMl = numAttr(effectiveState, 'lifetimeVolume', 'totalVolume') ?? 0
        const dailyMl = numAttr(effectiveState, 'dailyConsumption', 'dailyVolume') ?? 0
        const bottlesSavedMonth = Math.round(monthlyMl / 500)
        const dailyGoalPct = Math.min(100, Math.round((dailyMl / 2000) * 100))
        const lifetimeLiters = lifetimeMl / 1000
        const lifetimeDisplay = lifetimeLiters >= 1000 ? `${(lifetimeLiters / 1000).toFixed(0)}k` : `${lifetimeLiters.toFixed(0)}`
        return (
          <div key="header" className={`wd3__banner${hl('header')}`} data-modal-section="header">
            {/* Background image — from deviceState.bannerImageUrl or default */}
            <div className="wd3__banner-bg">
              <img
                src={String(getAttr(effectiveState, 'bannerImageUrl', 'headerImage') || 'https://lh3.googleusercontent.com/aida-public/AB6AXuAk9L7WoBKeF3jZcPHgPGQf_d4bTDFALHt28TghHMfpepB-2G8aDGyfUs_Q35_LSx8RCrTXUeR9YVYF5OTzNrMfdsaUKoJrxj4t3iGiygNAjkmKpwk4liTZa1BGRbaY_bbwW9vi1-lISRd3DhYSyrPtE5jS0WabZ7_xi743c42NG5-bXQzvoD4kZ1IX77aDctYMqgJ0KSwkhTCFudNWqPz7JdzfBjFopcDTnBGeGMyRU3iqxQiOqKT0Jr50IMr9YUecwmeH3b4RsWs')}
                alt=""
              />
            </div>
            {/* Content */}
            <div className="wd3__banner-content">
              <div className="wd3__banner-top">
                <h1 className="wd3__banner-name" data-modal-slot="header-name">{displayName}</h1>
                <div className="wd3__banner-top-right">
                  <div className="wd3__banner-badge" data-modal-slot="header-status">
                    <span className={`wd3__status-dot${device.online !== false ? ' wd3__status-dot--online' : ''}`} data-modal-slot="header-dot" />
                    <span>{device.online !== false ? 'Online' : 'Offline'}</span>
                  </div>
                </div>
              </div>
              {/* Frosted glass eco sub-card */}
              <div className="wd3__banner-eco">
                <div className="wd3__banner-eco-top">
                  <div className="wd3__banner-eco-icon-wrap">
                    <span className="material-symbols-outlined" data-modal-slot="header-eco-icon">eco</span>
                  </div>
                  <p className="wd3__banner-eco-msg" data-modal-slot="header-eco-msg">
                    {`"Saved ${bottlesSavedMonth} plastic bottles this month."`}
                  </p>
                </div>
                <div className="wd3__banner-eco-pills">
                  <span className="wd3__banner-pill wd3__banner-pill--secondary" data-modal-slot="header-eco-stat">
                    {dailyGoalPct}% Goal
                  </span>
                  <span className="wd3__banner-pill wd3__banner-pill--primary" data-modal-slot="header-eco-stat-label">
                    {lifetimeDisplay} Lifetime
                  </span>
                </div>
              </div>
            </div>
          </div>
        )
      }

      case 'purity': {
        const dailyLiters = ((numAttr(effectiveState, 'dailyConsumption', 'dailyVolume') ?? 0) / 1000).toFixed(1)
        const dailyPct = Math.min(100, Math.round(((numAttr(effectiveState, 'dailyConsumption', 'dailyVolume') ?? 0) / 2000) * 100))
        return (
          <div key="purity" className={`wd3__hydration${hl('purity')}`} data-modal-section="purity">
            <div className="wd3__hydration-label" data-modal-slot="purity-label">Current Status</div>
            <div className="wd3__hydration-value">
              <span className="wd3__hydration-number" data-modal-slot="purity-number">{dailyLiters}</span>
              <span className="wd3__hydration-unit" data-modal-slot="purity-unit">Liters Today</span>
            </div>
            <div className="wd3__hydration-bar" data-modal-slot="purity-bar">
              <div className="wd3__hydration-bar-fill" style={{ width: `${dailyPct}%` }} />
            </div>
            <div className="wd3__hydration-range" data-modal-slot="purity-range">
              <span>0L</span>
              <span>Target 2L</span>
            </div>
          </div>
        )
      }

      case 'status': {
        const outletIcons: Record<string, string> = { ice: 'ac_unit', warm: 'water_drop', hot: 'local_fire_department' }
        const outletLabels: Record<string, string> = { ice: 'Cold', warm: 'Ambient', hot: 'Hot' }
        return (
          <div key="status" className={`wd3__status-card${hl('status')}`} data-modal-section="status">
            <div className="wd3__status-card-top">
              <span className="wd3__status-card-label" data-modal-slot="status-label">Current Status</span>
              <span className="wd3__status-card-icon material-symbols-outlined" data-modal-slot="status-icon">opacity</span>
            </div>
            <div className="wd3__status-outlets">
              {activeOutlets.map(outlet => {
                const temp = numAttr(effectiveState, ...outlet.tempAttrKeys)
                const target = numAttr(effectiveState, ...outlet.targetAttrKeys)
                const isSelected = outlet.id === selectedOutletId
                // Determine if actively heating/cooling
                let statusText = outletLabels[outlet.id] || outlet.label
                if (displayPower && temp != null && target != null && outlet.id !== 'warm') {
                  if (outlet.id === 'ice' && temp > target) statusText = 'Cooling...'
                  else if (outlet.id === 'hot' && temp < target) statusText = 'Heating...'
                  else statusText = 'Ready'
                }
                const level = numAttr(effectiveState, ...outlet.levelAttrKeys)
                return (
                  <div key={outlet.id}
                    className="wd3__status-outlet"
                    data-outlet={outlet.id}
                    data-modal-slot="status-mode">
                    <span className="wd3__status-outlet-icon material-symbols-outlined" data-modal-slot="status-outlet-icon">
                      {outletIcons[outlet.id] || 'water_drop'}
                    </span>
                    <span className="wd3__status-outlet-temp" data-modal-slot="status-outlet-temp">{displayPower ? `${temp ?? '--'}°` : '--'}</span>
                    {level != null && (
                      <div className="wd3__status-outlet-level" data-modal-slot="status-outlet-level">
                        <div className="wd3__status-outlet-level-fill" style={{ width: `${Math.max(4, Math.min(100, level))}%` }} />
                      </div>
                    )}
                    <span className="wd3__status-outlet-label" data-modal-slot="status-outlet-label">{statusText}</span>
                  </div>
                )
              })}
            </div>
          </div>
        )
      }

      case 'tds': {
        const tdsVal = numAttr(effectiveState, 'tds', 'tdsValue', 'tds_pure', 'waterPurity')
        const tdsRaw = numAttr(effectiveState, 'tds_raw')
        return (
          <div key="tds" className={`wd3__tds-row${hl('tds')}`} data-modal-section="tds">
            <span className="wd3__tds-icon material-symbols-outlined" data-modal-slot="tds-icon">water_drop</span>
            <div className="wd3__tds-info">
              <span className="wd3__tds-label" data-modal-slot="tds-label">Water Purity (TDS)</span>
              <div className="wd3__tds-values">
                <span className="wd3__tds-value" data-modal-slot="tds-pure-value">
                  Pure <strong>{tdsVal != null ? tdsVal : '--'}</strong> ppm
                </span>
                {tdsRaw != null && (
                  <span className="wd3__tds-value wd3__tds-value--raw" data-modal-slot="tds-raw-value">
                    Source <strong>{tdsRaw}</strong> ppm
                  </span>
                )}
              </div>
            </div>
          </div>
        )
      }

      case 'controls': {
        return (
          <div key="controls" className={`wd3__controls${hl('controls')}`} data-modal-section="controls">
            <div className="wd3__controls-header">
              <div className="wd3__controls-title" data-modal-slot="controls-title">Temperature Control</div>
              <span className="wd3__controls-icon material-symbols-outlined" data-modal-slot="controls-icon">thermostat</span>
            </div>
            {activeOutlets.filter(o => o.id !== 'warm').map(outlet => {
              const temp = numAttr(effectiveState, ...outlet.tempAttrKeys)
              const serverTarget = numAttr(effectiveState, ...outlet.targetAttrKeys) ?? temp ?? outlet.min
              const displayTarget = pendingTargets[outlet.id] ?? serverTarget
              return (
                <div key={outlet.id} className="wd3__control-row">
                  <div className="wd3__control-top">
                    <span className="wd3__control-name" data-modal-slot="controls-name">{outlet.label}</span>
                    {/* Tap the temp number to open picker wheel */}
                    <span
                      className="wd3__control-temp" data-modal-slot="controls-range"
                      onClick={() => !isEditorMode && openPicker(outlet)}
                      style={{ cursor: displayPower ? 'pointer' : 'default' }}
                    >{displayTarget}°C</span>
                  </div>
                  <input
                    type="range"
                    className="wd3__control-slider" data-modal-slot="controls-slider"
                    min={outlet.min}
                    max={outlet.max}
                    step={outlet.step}
                    value={displayTarget}
                    onChange={e => {
                      if (isEditorMode) return
                      const val = Number(e.target.value)
                      if (!isNaN(val)) {
                        setPendingTargets(prev => ({ ...prev, [outlet.id]: val }))
                      }
                    }}
                    onMouseUp={e => {
                      if (isEditorMode) return
                      // Read value directly from event — avoids React 18 batching stale-state issue
                      const val = Number((e.target as HTMLInputElement).value)
                      if (!isNaN(val)) execCmd(outlet.setCmd, { temperature: val })
                    }}
                    onTouchEnd={e => {
                      if (isEditorMode) return
                      const val = Number((e.currentTarget as HTMLInputElement).value)
                      if (!isNaN(val)) execCmd(outlet.setCmd, { temperature: val })
                    }}
                    disabled={!displayPower}
                  />
                </div>
              )
            })}
            <button className="wd3__dispense-btn" data-modal-slot="controls-btn" disabled={!displayPower || isEditorMode} onClick={() => !isEditorMode && execCmd('reboil')}>
              Re-Boil
            </button>
          </div>
        )
      }

      case 'chart':
        return (
          <div key="chart" className={`wd3__chart${hl('chart')}`} data-modal-section="chart">
            <div className="wd3__chart-header">
              <div className="wd3__chart-header-left">
                <span className="wd3__chart-label" data-modal-slot="chart-label">Hydration Metrics</span>
                <span className="wd3__chart-title" data-modal-slot="chart-title">Consumption History</span>
              </div>
              <div className="wd3__chart-range">
                {(['day', 'week', 'month'] as const).map(r => (
                  <button key={r} className={`wd3__chart-range-btn${chartRange === r ? ' wd3__chart-range-btn--active' : ''}`}
                    data-modal-slot="chart-range-btn" onClick={() => setChartRange(r)}>
                    {r === 'day' ? 'Day' : r === 'week' ? 'Week' : 'Month'}
                  </button>
                ))}
              </div>
            </div>
            <div className="wd3__chart-bars">
              {chartData.map((d, i) => (
                <div key={i} className={`wd3__chart-bar${d.active ? ' wd3__chart-bar--active' : ''}`} data-modal-slot="chart-bar"
                  style={{ height: `${d.pct}%` }} />
              ))}
            </div>
          </div>
        )

      case 'childLock':
        return (
          <div key="childLock" className={`wd3__lock${hl('childLock')}`} data-modal-section="childLock">
            <div className="wd3__lock-left">
              <span className="wd3__lock-icon material-symbols-outlined" data-modal-slot="lock-icon" style={{ fontVariationSettings: "'FILL' 1" }}>enhanced_encryption</span>
              <span className="wd3__lock-label" data-modal-slot="lock-label">Safety Child Lock</span>
            </div>
            <button className={`wd3__toggle${displayLock ? ' wd3__toggle--on' : ''}`} data-modal-slot="lock-toggle" onClick={toggleChildLock}>
              <div className="wd3__toggle-knob" />
            </button>
          </div>
        )

      case 'filters': {
        const worstFilter = activeFilters.length > 0 ? activeFilters.reduce((a, b) => a.pct < b.pct ? a : b) : null
        return (
          <div key="filters" className={`wd3__filterlife${hl('filters')}`} data-modal-section="filters">
            <div className="wd3__filterlife-label" data-modal-slot="filter-name">Filter Life</div>
            <div className="wd3__filterlife-row">
              <span className="wd3__filterlife-icon material-symbols-outlined" data-modal-slot="filter-btn">opacity</span>
              <div className="wd3__filterlife-info">
                <span className="wd3__filterlife-pct" data-modal-slot="filter-pct">{worstFilter?.pct ?? '--'}%</span>
                <span className="wd3__filterlife-desc" data-modal-slot="filter-desc">Optimal Performance</span>
              </div>
            </div>
            <button className="wd3__filterlife-order" data-modal-slot="filter-order" onClick={() => {}}>
              Order Replacement
            </button>
          </div>
        )
      }

      default: return null
    }
  }

  return (
    <div className={`wd3 ${stateClass}${pickerOpen ? ' wd3--picker-open' : ''}`} data-modal-template-id="water-dispenser-standalone-v3">
      {cssOverrides && <style>{cssOverrides}</style>}
      {orderedSections.map(id => renderSection(id))}

      {/* Settings — expandable panel */}
      <button className="wd3__settings-btn" data-modal-slot="settings-btn"
        onClick={() => setSettingsOpen(!settingsOpen)} aria-label="Settings">
        <span className="material-symbols-outlined" style={{ fontSize: 22 }}>
          {settingsOpen ? 'close' : 'settings'}
        </span>
      </button>

      {settingsOpen && (
        <div className="wd3__settings-panel">
          <div className="wd3__settings-section">
            <div className="wd3__settings-section-title">Device Name</div>
            {isRenaming ? (
              <div className="wd3__settings-row">
                <input
                  ref={renameInputRef}
                  className="wd3__settings-input"
                  value={renameValue}
                  onChange={e => setRenameValue(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') submitRename(); if (e.key === 'Escape') setIsRenaming(false) }}
                  onBlur={submitRename}
                  maxLength={40}
                />
              </div>
            ) : (
              <div className="wd3__settings-row">
                <span className="wd3__settings-value">{displayName}</span>
                <button className="wd3__settings-edit" onClick={startRename}>
                  <span className="material-symbols-outlined" style={{ fontSize: 16 }}>edit</span>
                </button>
              </div>
            )}
          </div>
          <div className="wd3__settings-section">
            <div className="wd3__settings-section-title">Device Info</div>
            {deviceInfoItems.map(item => (
              <div key={item.label} className="wd3__settings-row">
                <span className="wd3__settings-label">{item.label}</span>
                <span className="wd3__settings-value">{item.value}</span>
                {item.action === 'copy' && (
                  <button className="wd3__settings-copy" onClick={() => copyToClipboard(item.value, item.label)}>
                    <span className="material-symbols-outlined" style={{ fontSize: 14 }}>content_copy</span>
                    {copiedField === item.label && <span className="wd3__settings-copied">Copied</span>}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Picker */}
      {pickerOpen && pickerOutlet && (
        <div className="wd3__picker-overlay" onClick={() => { setPickerOpen(false); setPickerOutlet(null) }}>
          <div className="wd3__picker" onClick={e => e.stopPropagation()}>
            <div className="wd3__picker-header">
              <span className="wd3__picker-title">{pickerOutlet.pickerTitle}</span>
              <button className="wd3__picker-done" onClick={confirmPicker}>Done</button>
            </div>
            <div className="wd3__picker-wheel" ref={pickerWheelRef}>
              <div style={{ height: 80 }} />
              {pickerItems.map(t => (
                <div key={t} className={`wd3__picker-item${t === pickerValue ? ' wd3__picker-item--selected' : ''}`}
                  onClick={() => setPickerValue(t)}>
                  {t}°C
                </div>
              ))}
              <div style={{ height: 80 }} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
